import {
  Decoration,
  EditorView,
  ViewPlugin,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view';
import {
  StateEffect,
  StateField,
  type EditorState,
  type Extension,
  type Range,
} from '@codemirror/state';
import { ensureSyntaxTree, syntaxTree } from '@codemirror/language';
import { parseFrontmatter, parseNote, titleFromPath, type NoteParse } from '@rbv/shared';
import { mediaKind, parseMarkdownTable, wikiDisplayText } from './helpers.js';
import {
  BulletWidget,
  CheckboxWidget,
  CodeLangWidget,
  HrWidget,
  LinkWidget,
  MediaWidget,
  NoteEmbedWidget,
  TableWidget,
  embedContent,
} from './widgets.js';
import { vaultFileUrl } from '../navigation.js';
import { vault } from '../sync/vault.js';
import type { EditorCtx, NavTarget } from './context.js';

/**
 * Obsidian-style live preview: the markdown text IS the document; decorations
 * hide/replace syntax except where the cursor/selection touches the element.
 */

/** Dispatched when link resolution or embedded note content changed. */
export const refreshLivePreview = StateEffect.define<null>();

const HIDE = Decoration.replace({});

function build(view: EditorView, parse: NoteParse, ctx: EditorCtx): DecorationSet {
  const { state } = view;
  const doc = state.doc;
  const sel = state.selection;
  const decos: Range<Decoration>[] = [];
  const lineClasses = new Map<number, Set<string>>();
  const skips: Array<[number, number]> = [];

  const touches = (from: number, to: number) =>
    sel.ranges.some((r) => r.from <= to && r.to >= from);
  const touchesLines = (from: number, to: number) =>
    touches(doc.lineAt(from).from, doc.lineAt(Math.min(to, doc.length)).to);
  const overlapsSkip = (from: number, to: number) =>
    skips.some(([a, b]) => from < b && to > a);
  const insideWiki = (from: number, to: number) =>
    parse.wikilinks.some((w) => from >= w.start && to <= w.end);

  const hide = (from: number, to: number) => {
    if (to > from) decos.push(HIDE.range(from, to));
  };
  const mark = (from: number, to: number, cls: string) => {
    if (to > from) decos.push(Decoration.mark({ class: cls }).range(from, to));
  };
  const addLineClass = (pos: number, cls: string) => {
    const from = doc.lineAt(pos).from;
    let set = lineClasses.get(from);
    if (!set) lineClasses.set(from, (set = new Set()));
    for (const c of cls.split(' ')) set.add(c);
  };
  const eachLine = (from: number, to: number, cls: string) => {
    let pos = from;
    while (pos <= to && pos <= doc.length) {
      const line = doc.lineAt(pos);
      addLineClass(line.from, cls);
      if (line.to >= to) break;
      pos = line.to + 1;
    }
  };
  /** Hide a mark node plus one trailing space when present. */
  const hideMarkPlusSpace = (from: number, to: number) => {
    let end = to;
    if (doc.sliceString(end, end + 1) === ' ') end++;
    hide(from, end);
  };

  const fmEnd = parse.bodyStart;
  if (fmEnd > 0) eachLine(0, Math.max(0, fmEnd - 1), 'cm-frontmatter');

  const mdByStart = new Map(parse.mdLinks.map((l) => [l.start, l]));

  for (const range of view.visibleRanges) {
    syntaxTree(state).iterate({
      from: range.from,
      to: range.to,
      enter: (node) => {
        if (fmEnd > 0 && node.from < fmEnd && node.name !== 'Document') return false;

        const atx = /^ATXHeading([1-6])$/.exec(node.name);
        if (atx) {
          const line = doc.lineAt(node.from);
          addLineClass(line.from, `cm-heading cm-h${atx[1]}`);
          if (!touches(line.from, line.to)) {
            const hm = node.node.getChild('HeaderMark');
            if (hm) hideMarkPlusSpace(hm.from, hm.to);
          }
          return;
        }

        const setext = /^SetextHeading([12])$/.exec(node.name);
        if (setext) {
          // Underline-line collapse lives in the block field (spans a line
          // break — not allowed from a view plugin); classes only here.
          const hm = node.node.getChild('HeaderMark');
          const textEnd = hm ? Math.max(node.from, hm.from - 1) : node.to;
          eachLine(node.from, Math.max(node.from, textEnd - 1), `cm-heading cm-h${setext[1]}`);
          return;
        }

        switch (node.name) {
          case 'Emphasis':
          case 'StrongEmphasis':
          case 'Strikethrough':
          case 'Highlight': {
            const cls =
              node.name === 'Emphasis'
                ? 'cm-em'
                : node.name === 'StrongEmphasis'
                  ? 'cm-strong'
                  : node.name === 'Strikethrough'
                    ? 'cm-strike'
                    : 'cm-hl';
            mark(node.from, node.to, cls);
            if (!touches(node.from, node.to)) {
              const markName =
                node.name === 'Strikethrough'
                  ? 'StrikethroughMark'
                  : node.name === 'Highlight'
                    ? 'HighlightMark'
                    : 'EmphasisMark';
              for (const m of node.node.getChildren(markName)) hide(m.from, m.to);
            }
            return;
          }

          case 'InlineCode': {
            mark(node.from, node.to, 'cm-inline-code');
            if (!touches(node.from, node.to)) {
              for (const m of node.node.getChildren('CodeMark')) hide(m.from, m.to);
            }
            return false;
          }

          case 'FencedCode': {
            const first = doc.lineAt(node.from);
            const last = doc.lineAt(Math.min(node.to, doc.length));
            eachLine(node.from, node.to, 'cm-codeblock');
            addLineClass(first.from, 'cm-codeblock-begin');
            addLineClass(last.from, 'cm-codeblock-end');
            skips.push([node.from, node.to]);
            if (!touches(first.from, last.to)) {
              const info = node.node.getChild('CodeInfo');
              const lang = info ? doc.sliceString(info.from, info.to) : '';
              if (first.to > first.from) {
                decos.push(
                  Decoration.replace({ widget: new CodeLangWidget(lang) }).range(
                    first.from,
                    first.to,
                  ),
                );
              }
              const fenceMarks = node.node.getChildren('CodeMark');
              if (fenceMarks.length >= 2 && last.from > first.from) {
                hide(last.from, last.to);
              }
            }
            return false;
          }

          case 'CodeBlock': {
            eachLine(node.from, node.to, 'cm-codeblock');
            skips.push([node.from, node.to]);
            return false;
          }

          case 'Blockquote': {
            eachLine(node.from, node.to, 'cm-blockquote');
            return;
          }

          case 'QuoteMark': {
            const line = doc.lineAt(node.from);
            if (!touches(line.from, line.to)) hideMarkPlusSpace(node.from, node.to);
            return;
          }

          case 'ListMark': {
            const line = doc.lineAt(node.from);
            const markText = doc.sliceString(node.from, node.to);
            const isTask = node.node.nextSibling?.name === 'Task';
            mark(node.from, node.to, 'cm-list-mark');
            if (!touches(line.from, line.to)) {
              if (isTask) {
                hideMarkPlusSpace(node.from, node.to);
              } else if (!/^\d/.test(markText)) {
                decos.push(
                  Decoration.replace({ widget: new BulletWidget() }).range(node.from, node.to),
                );
              }
            }
            return;
          }

          case 'TaskMarker': {
            const line = doc.lineAt(node.from);
            const checked =
              doc.sliceString(node.from + 1, node.from + 2).toLowerCase() === 'x';
            if (!touches(line.from, line.to)) {
              let end = node.to;
              if (doc.sliceString(end, end + 1) === ' ') end++;
              decos.push(
                Decoration.replace({ widget: new CheckboxWidget(checked) }).range(node.from, end),
              );
            }
            if (checked) {
              let s = node.to;
              if (doc.sliceString(s, s + 1) === ' ') s++;
              mark(s, line.to, 'cm-task-done');
            }
            return;
          }

          case 'HorizontalRule': {
            const line = doc.lineAt(node.from);
            if (!touches(line.from, line.to)) {
              decos.push(Decoration.replace({ widget: new HrWidget() }).range(node.from, node.to));
            }
            return;
          }

          case 'Table': {
            // Replacement by a rendered widget lives in the block field
            // (multi-line replace); here: skip inline decorations inside and
            // style the raw aligned source while the cursor is in the table.
            skips.push([node.from, node.to]);
            if (touchesLines(node.from, node.to)) {
              eachLine(node.from, node.to, 'cm-table-src');
            }
            return false;
          }

          case 'Link': {
            if (insideWiki(node.from, node.to)) return;
            const sn = node.node;
            const marks = sn.getChildren('LinkMark');
            const url = sn.getChild('URL');
            if (marks.length < 2) return;
            const info = mdByStart.get(node.from);
            const href = info?.href ?? (url ? doc.sliceString(url.from, url.to) : '');
            if (!href) return;
            const internal = info ? info.internal : false;
            const labelFrom = marks[0].to;
            const labelTo = marks[1].from;
            if (!touches(node.from, node.to)) {
              if (labelTo > labelFrom) {
                decos.push(
                  Decoration.mark({
                    class: internal ? 'cm-mdlink cm-mdlink-internal' : 'cm-mdlink',
                    attributes: {
                      'data-href': href,
                      'data-internal': internal ? '1' : '0',
                    },
                  }).range(labelFrom, labelTo),
                );
              }
              hide(marks[0].from, marks[0].to);
              hide(marks[1].from, node.to);
            } else {
              mark(labelFrom, labelTo, 'cm-mdlink');
              if (url) mark(url.from, url.to, 'cm-url');
            }
            return;
          }

          case 'Image': {
            if (insideWiki(node.from, node.to)) return;
            const sn = node.node;
            const url = sn.getChild('URL');
            const href = url ? doc.sliceString(url.from, url.to) : '';
            const oneLine = doc.lineAt(node.from).to >= node.to;
            if (href && oneLine && !touches(doc.lineAt(node.from).from, doc.lineAt(node.to).to)) {
              const external = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href) || href.startsWith('//');
              const marks = sn.getChildren('LinkMark');
              const alt =
                marks.length >= 2 ? doc.sliceString(marks[0].to, marks[1].from) : '';
              let src: string | null = null;
              let kind = mediaKind(href);
              if (external) {
                src = href;
                kind = kind ?? 'image';
              } else {
                const res = ctx.resolve(decodeURIComponent(href));
                if (res.path) {
                  kind = mediaKind(res.path);
                  if (kind) src = vaultFileUrl(res.path);
                }
              }
              if (src && kind) {
                decos.push(
                  Decoration.replace({ widget: new MediaWidget(src, kind, alt) }).range(
                    node.from,
                    node.to,
                  ),
                );
              }
            }
            return false;
          }

          case 'URL': {
            const parent = node.node.parent?.name;
            if (parent === 'Link' || parent === 'Image') return;
            const href = doc.sliceString(node.from, node.to);
            decos.push(
              Decoration.mark({
                class: 'cm-mdlink',
                attributes: {
                  'data-href': /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href) ? href : `https://${href}`,
                  'data-internal': '0',
                },
              }).range(node.from, node.to),
            );
            return;
          }
        }
        return;
      },
    });
  }

  // ---- parse-based decorations (wikilinks, tags, block ids) ----------------
  const vFrom = view.visibleRanges.length ? view.visibleRanges[0].from : 0;
  const vTo = view.visibleRanges.length
    ? view.visibleRanges[view.visibleRanges.length - 1].to
    : doc.length;

  for (const w of parse.wikilinks) {
    if (w.end < vFrom || w.start > vTo || w.end > doc.length) continue;
    if (overlapsSkip(w.start, w.end)) continue;
    if (touches(w.start, w.end)) {
      mark(w.start, w.end, 'cm-wikilink-raw');
      continue;
    }
    const res = ctx.resolve(w.target);
    const nav: NavTarget = { target: w.target, heading: w.heading, blockId: w.blockId };
    if (res.id) nav.id = res.id;
    const uploading = /^uploading-/.test(w.target);
    let widget;
    if (!w.embed) {
      widget = new LinkWidget(wikiDisplayText(w), !res.id, nav, ctx, uploading);
    } else if (res.id && res.path) {
      const kind = mediaKind(res.path);
      const entry = vault.getEntry(res.id);
      if (kind) {
        widget = new MediaWidget(vaultFileUrl(res.path), kind, w.alias ?? w.target);
      } else if (entry?.kind === 'note') {
        const title = w.alias ?? wikiDisplayText({ ...w, target: titleFromPath(res.path) });
        widget = new NoteEmbedWidget(
          res.id,
          title,
          embedContent(res.id, w.heading, w.blockId),
          nav,
          ctx,
        );
      } else {
        widget = new LinkWidget(wikiDisplayText(w), false, nav, ctx);
      }
    } else {
      widget = new LinkWidget(wikiDisplayText(w), true, nav, ctx, uploading);
    }
    decos.push(Decoration.replace({ widget }).range(w.start, w.end));
  }

  for (const t of parse.tags) {
    if (t.end < vFrom || t.start > vTo || t.end > doc.length) continue;
    if (overlapsSkip(t.start, t.end)) continue;
    mark(t.start, t.end, 'cm-tag');
  }

  for (const b of parse.blocks) {
    if (b.end < vFrom || b.start > vTo || b.end > doc.length) continue;
    if (overlapsSkip(b.start, b.end)) continue;
    mark(b.start, b.end, 'cm-blockid');
  }

  for (const [pos, set] of lineClasses) {
    decos.push(Decoration.line({ class: [...set].join(' ') }).range(pos));
  }

  return Decoration.set(decos, true);
}

/**
 * Multi-line replacing decorations (rendered tables, setext underline
 * collapse) must be provided by a state field, not a view plugin — plugins
 * may not hide line breaks. Tables are rare, so a whole-doc scan per
 * transaction is cheap.
 */
function blockDecoField(ctx: EditorCtx): StateField<DecorationSet> {
  const compute = (state: EditorState): DecorationSet => {
    const doc = state.doc;
    const sel = state.selection;
    const decos: Range<Decoration>[] = [];
    // Lezer parses the `title: x\n---` frontmatter close as a SetextHeading2
    // whose HeaderMark is the closing fence; collapsing it would hide the
    // fence. Never treat anything inside the frontmatter block as a table or
    // setext heading.
    const bodyStart = parseFrontmatter(doc.sliceString(0, Math.min(doc.length, 8192))).bodyStart;
    const touchesLines = (from: number, to: number) => {
      const a = doc.lineAt(from).from;
      const b = doc.lineAt(Math.min(to, doc.length)).to;
      return sel.ranges.some((r) => r.from <= b && r.to >= a);
    };
    const tree = ensureSyntaxTree(state, doc.length, 100) ?? syntaxTree(state);
    tree.iterate({
      enter: (node) => {
        if (node.from < bodyStart) return;
        if (node.name === 'Table') {
          if (!touchesLines(node.from, node.to)) {
            const text = doc.sliceString(node.from, node.to);
            const parsed = parseMarkdownTable(text);
            if (parsed) {
              decos.push(
                Decoration.replace({ widget: new TableWidget(text, parsed, ctx) }).range(
                  node.from,
                  node.to,
                ),
              );
            }
          }
          return false;
        }
        if (/^SetextHeading[12]$/.test(node.name)) {
          const hm = node.node.getChild('HeaderMark');
          if (hm && hm.from > node.from && !touchesLines(node.from, node.to)) {
            // Collapse the ===/--- underline line into the heading line.
            decos.push(Decoration.replace({}).range(hm.from - 1, hm.to));
          }
          return false;
        }
        return;
      },
    });
    return Decoration.set(decos, true);
  };

  return StateField.define<DecorationSet>({
    create: compute,
    update(deco, tr) {
      if (
        tr.docChanged ||
        tr.selection ||
        tr.effects.some((ef) => ef.is(refreshLivePreview))
      ) {
        return compute(tr.state);
      }
      return deco;
    },
    provide: (f) => EditorView.decorations.from(f),
  });
}

function handleMdLinkClick(e: MouseEvent, view: EditorView, ctx: EditorCtx): boolean {
  const target = e.target as HTMLElement | null;
  const elm = target?.closest?.('.cm-mdlink[data-href]') as HTMLElement | null;
  if (!elm || !view.contentDOM.contains(elm)) return false;
  const href = elm.dataset.href;
  if (!href) return false;
  e.preventDefault();
  if (elm.dataset.internal === '1') {
    ctx.navigate({ target: decodeURIComponent(href) });
  } else {
    window.open(href, '_blank', 'noopener,noreferrer');
  }
  return true;
}

export function livePreview(ctx: EditorCtx): Extension {
  const plugin = ViewPlugin.define(
    (view) => {
      const value = {
        parse: parseNote(view.state.doc.toString()),
        decorations: Decoration.none as DecorationSet,
        update(u: ViewUpdate) {
          const refresh = u.transactions.some((tr) =>
            tr.effects.some((ef) => ef.is(refreshLivePreview)),
          );
          if (u.docChanged) value.parse = parseNote(u.state.doc.toString());
          if (u.docChanged || u.selectionSet || u.viewportChanged || refresh) {
            value.decorations = build(u.view, value.parse, ctx);
          }
        },
      };
      value.decorations = build(view, value.parse, ctx);
      return value;
    },
    {
      decorations: (v) => v.decorations,
      eventHandlers: {
        mousedown(e, view) {
          return handleMdLinkClick(e, view, ctx);
        },
      },
    },
  );
  return [blockDecoField(ctx), plugin];
}
